import { logout } from '@foodsby/webapp-jwt'
import { push } from 'connected-react-router'
import { first, isEmpty, isString } from 'lodash'
import { all, call, fork, put, race, select, take, takeLatest } from 'redux-saga/effects'

import { eventChannel } from 'redux-saga'
import { resetCaches } from '../../api/api'
import { friendsRoute, locationRoute, loginRoute } from '../../routes/routes'
import {
  getUserConsent,
  loginUser,
  logoutAdmin,
  oneShotRegister,
  updateUserConsent,
  updateUserLocation,
} from '../../services/user'
import { getConsentSyncBody, onConsentChange, whenAirgapReady } from '../../util/transcend'
import {
  logException,
  normalizeErrorResponse,
  normalizeLoginUserError,
  normalizeUserCreationError,
} from '../../util/errorUtils'
import { formatUrl } from '../../util/formatUtils'
import { pushGTMDataLayer } from '../../util/gtmUtils'
import { getDecodedAccessToken, storeUserInfoCookie } from '../../util/webStorageUtils'
import { FULFILLED } from '../utils'
import { clearPendingReferralCode } from '../../util/referralUtils'
import { confirmReferralSuccess, referralRedeem } from './friends'
import { JOIN_USER_SUCCESS, joinUser } from './join'
import {
  clearPreviousLocation,
  createUserSavedLocationStart,
  LOAD_LOCATION_SUCCESS,
  loadLocationStart,
  loadUserSavedLocationsStart,
} from './location'
import { SUCCESS } from './locationSearch'
import { PROFILE_SET } from './profile'
import { apiSaga, formSaga } from './sagas'
import {
  getLocationIdParam,
  selectConfirmUserCredentials,
  selectCurrentLocationWithAddress,
  selectCurrentUser,
  selectEntryPoint,
  selectIsAuthenticated,
  selectRef,
  selectReferralCode,
} from './selectors'
import { refreshTokenSaga } from './token'
import { enqueueSnackbar } from './snackbar'

export const ROLE_FOODSBY_ADMIN = 'ROLE_FOODSBY_ADMIN'

// ------------------------------------
// Action Types & Creators
// ------------------------------------

export const LOGIN = 'foodsby/user/LOGIN'
export const LOGIN_SUCCESS = 'foodsby/user/LOGIN_SUCCESS'
export const LOGIN_FAILURE = 'foodsby/user/LOGIN_FAILURE'

export const LOGOUT = 'foodsby/user/LOGOUT'
export const LOGOUT_SUCCESS = 'foodsby/user/LOGOUT_SUCCESS'
export const LOGOUT_FAILURE = 'foodsby/user/LOGOUT_FAILURE'

export const GET_CURRENT_USER = 'foodsby/user/GET_CURRENT_USER'
export const GET_CURRENT_USER_SUCCESS = 'foodsby/user/GET_CURRENT_USER_SUCCESS'
export const GET_CURRENT_USER_FAILURE = 'foodsby/user/GET_CURRENT_USER_FAILURE'

export const CONFIRM_USER = 'foodsby/user/CONFIRM_USER'
export const CONFIRM_USER_SUCCESS = 'foodsby/user/CONFIRM_USER_SUCCESS'
export const CONFIRM_USER_FAILURE = 'foodsby/user/CONFIRM_USER_FAILURE'

export const REGISTER_LIGHT = 'foodsby/ticket/register/REGISTER_LIGHT'
export const REGISTER_LIGHT_SUCCESS = 'foodsby/ticket/register/REGISTER_LIGHT_SUCCESS'
export const REGISTER_LIGHT_FAILURE = 'foodsby/ticket/register/REGISTER_LIGHT_FAILURE'

export const UPDATE_AND_SAVE_USER_LOCATION = 'foodsby/user/UPDATE_AND_SAVE_USER_LOCATION'
export const UPDATE_AND_SAVE_USER_LOCATION_SUCCESS =
  'foodsby/user/UPDATE_AND_SAVE_USER_LOCATION_SUCCESS'
export const UPDATE_AND_SAVE_USER_LOCATION_FAILURE =
  'foodsby/user/UPDATE_AND_SAVE_USER_LOCATION_FAILURE'

export const UPDATE_USER_LOCATION_TEMPORARILY = 'foodsby/user/UPDATE_USER_LOCATION_TEMPORARILY'
export const REVERT_TEMPORARY_LOCATION = 'foodsby/user/REVERT_TEMPORARY_LOCATION'

export const CLEAR_LOGIN_ERRORS = 'foodsby/user/CLEAR_LOGIN_ERRORS'
export const CLEAR_UPDATE_LOCATION_ERROR = '/foodsby/user/CLEAR_UPDATE_LOCATION_ERROR'

export const SET_ENTRY_POINT = '/foodsby/user/SET_ENTRY_POINT'
export const SET_REFERRAL_CODE = '/foodsby/user/SET_REFERRAL_CODE'

export const loginStart = (credentials, ref) => {
  return {
    payload: { credentials, ref },
    type: LOGIN,
  }
}

export const loginSuccess = response => {
  return {
    payload: response,
    type: LOGIN_SUCCESS,
  }
}

export const loginFailure = error => {
  const message = normalizeErrorResponse(error.response, error.message)
  return {
    error: { message },
    type: LOGIN_FAILURE,
  }
}

export const logoutStart = (ref, currentUser) => {
  return {
    payload: { currentUser, ref },
    type: LOGOUT,
  }
}

export const logoutSuccess = () => {
  return {
    type: LOGOUT_SUCCESS,
  }
}

export const logoutFailure = error => {
  return {
    error,
    type: LOGOUT_FAILURE,
  }
}

export const currentUserStart = () => {
  return {
    type: GET_CURRENT_USER,
  }
}

export const registerLightStart = userData => {
  return {
    payload: { userData },
    type: REGISTER_LIGHT,
  }
}

export const registerLightSuccess = ({ username }) => {
  return {
    payload: { username },
    type: REGISTER_LIGHT_SUCCESS,
  }
}

export const registerLightFailure = error => {
  return {
    error,
    type: REGISTER_LIGHT_FAILURE,
  }
}

export const confirmRegisterStart = (formData, path, entryPoint, referralCode) => {
  return {
    payload: { entryPoint, formData, path, referralCode },
    type: CONFIRM_USER,
  }
}

export const confirmRegisterSuccess = (currentUser, referralCode) => {
  return {
    payload: { currentUser, referralCode },
    type: CONFIRM_USER_SUCCESS,
  }
}

export const confirmRegisterFailure = error => {
  return {
    error,
    type: CONFIRM_USER_FAILURE,
  }
}

export const currentUserSuccess = currentUser => {
  return {
    payload: { currentUser },
    type: GET_CURRENT_USER_SUCCESS,
  }
}

export const currentUserFailure = error => {
  return {
    error,
    type: GET_CURRENT_USER_FAILURE,
  }
}

export const updateUserLocationTemporarily = locationId => {
  return {
    payload: { locationId },
    type: UPDATE_USER_LOCATION_TEMPORARILY,
  }
}

export const updateAndSaveUserLocationStart = locationId => {
  return {
    payload: { locationId },
    type: UPDATE_AND_SAVE_USER_LOCATION,
  }
}

export const updateAndSaveUserLocationSuccess = currentUser => {
  return {
    payload: { currentUser },
    type: UPDATE_AND_SAVE_USER_LOCATION_SUCCESS,
  }
}

export const updateAndSaveUserLocationFailure = error => {
  return {
    error,
    type: UPDATE_AND_SAVE_USER_LOCATION_FAILURE,
  }
}

export const revertTemporaryLocation = originalLocationId => {
  return {
    payload: { originalLocationId },
    type: REVERT_TEMPORARY_LOCATION,
  }
}

export const clearLoginErrors = () => {
  return {
    payload: {},
    type: CLEAR_LOGIN_ERRORS,
  }
}

export const clearUpdateLocationError = () => {
  return {
    type: CLEAR_UPDATE_LOCATION_ERROR,
  }
}

export const setEntryPoint = entryPoint => {
  return {
    payload: { entryPoint },
    type: SET_ENTRY_POINT,
  }
}

export const setReferralCode = referralCode => {
  return {
    payload: { referralCode },
    type: SET_REFERRAL_CODE,
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [CLEAR_LOGIN_ERRORS]: state => {
    return {
      ...state,
      error: undefined,
    }
  },
  [CLEAR_UPDATE_LOCATION_ERROR]: state => {
    return {
      ...state,
      updateUserLocationError: undefined,
    }
  },
  [CONFIRM_USER]: (state, action) => {
    const confirmCredentials = action.payload
    return {
      ...state,
      confirmCredentials,
      submitting: true,
    }
  },
  [CONFIRM_USER_FAILURE]: (state, action) => {
    const error = normalizeUserCreationError(action.error)
    return {
      ...state,
      confirmUserError: error,
      currentUser: undefined,
      submitting: undefined,
    }
  },
  [CONFIRM_USER_SUCCESS]: state => {
    return {
      ...state,
      confirmUserError: undefined,
      submitting: undefined,
    }
  },
  [GET_CURRENT_USER]: (state = { currentUser: undefined }) => {
    return {
      ...state,
      isCurrentUserLoading: true,
    }
  },
  [GET_CURRENT_USER_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      currentUser: undefined,
      error,
      isAuthenticated: false,
      isCurrentUserLoading: false,
      userInitialized: true,
    }
  },
  [GET_CURRENT_USER_SUCCESS]: (state, action) => {
    return {
      ...state,
      currentUser: action.payload.currentUser,
      userInitialized: true,
      error: undefined,
      isAuthenticated: true,
      isCurrentUserLoading: false,
    }
  },
  [JOIN_USER_SUCCESS]: (state, action) => {
    return {
      ...state,
      currentUser: action.payload.currentUser,
      isAuthenticated: true,
    }
  },
  [LOGIN]: (state, action) => {
    const { accessToken } = action.payload
    return {
      ...state,
      accessToken,
      currentUser: undefined,
      isAuthenticated: false,
      submitting: true,
    }
  },
  [LOGIN_FAILURE]: (state, action) => {
    const { error } = action
    const { numberOfFailedLoginAttempts } = state
    return {
      ...state,
      currentUser: undefined,
      error,
      isAuthenticated: false,
      submitting: undefined,
      numberOfFailedLoginAttempts: numberOfFailedLoginAttempts + 1,
    }
  },
  [LOGIN_SUCCESS]: state => {
    try {
      const { authorities, location_id, user_id, user_name } = getDecodedAccessToken()

      let currentUser = {
        authorities,
        deliveryLocationId: location_id,
        email: user_name,
        userId: user_id,
        userName: user_name,
      }

      return {
        ...state,
        confirmCredentials: undefined,
        currentUser,
        error: undefined,
        isAuthenticated: true,
        isCurrentUserLoading: false,
        ref: undefined,
        submitting: undefined,
        userInitialized: true,
      }
    } catch (ex) {
      logException(ex)
      return {
        ...state,
        error: normalizeLoginUserError(ex),
        submitting: undefined,
      }
    }
  },
  [LOGOUT]: (state, action) => {
    return {
      ...state,
      currentUser: undefined,
      isAuthenticated: false,
      ref: action.payload.ref,
    }
  },
  // Assuming the user is returned when registration finishes
  // Assuming user can register full or partial (w/password or w/o-password both are same call.
  [LOGOUT_FAILURE]: state => {
    return {
      ...state,
      currentUser: undefined,
      isAuthenticated: false,
    }
  },
  [LOGOUT_SUCCESS]: state => {
    return {
      ...state,
      currentUser: undefined,
      isAuthenticated: false,
    }
  },
  [REGISTER_LIGHT]: state => {
    return {
      ...state,
      registerLightError: undefined,
      submitting: true,
    }
  },
  [REGISTER_LIGHT_FAILURE]: (state, action) => {
    const error = normalizeUserCreationError(action.error.response)
    return {
      ...state,
      currentUser: undefined,
      isAuthenticated: false,
      registerLightError: error,
      submitting: undefined,
    }
  },
  [REGISTER_LIGHT_SUCCESS]: state => {
    return {
      ...state,
    }
  },
  [REVERT_TEMPORARY_LOCATION]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...state.currentUser,
        deliveryLocationId: action.payload.originalLocationId,
        originalLocationId: undefined,
      },
      hasChangedLocation: false,
    }
  },
  [SET_ENTRY_POINT]: (state, action) => {
    return {
      ...state,
      entryPoint: action.payload.entryPoint,
    }
  },
  [SET_REFERRAL_CODE]: (state, action) => {
    return {
      ...state,
      referralCode: action.payload.referralCode,
    }
  },
  [SUCCESS]: state => {
    return {
      ...state,
      hasChangedLocation: false,
    }
  },
  [UPDATE_AND_SAVE_USER_LOCATION]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...state.currentUser,
        deliveryLocationId: action.payload.locationId,
        originalLocationId: state.currentUser.deliveryLocationId,
      },
      hasChangedLocation: true,
      updateUserLocationError: undefined,
    }
  },
  [UPDATE_AND_SAVE_USER_LOCATION_FAILURE]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...state.currentUser,
        deliveryLocationId: state.currentUser.originalLocationId,
        originalLocationId: undefined,
      },
      updateUserLocationError: action.error,
    }
  },
  [UPDATE_AND_SAVE_USER_LOCATION_SUCCESS]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...action.payload.currentUser,
        originalLocationId: undefined,
      },
      hasChangedLocation: true,
    }
  },
  [UPDATE_USER_LOCATION_TEMPORARILY]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...state.currentUser,
        deliveryLocationId: action.payload.locationId,
        originalLocationId:
          state.currentUser.originalLocationId || state.currentUser.deliveryLocationId,
      },
      hasChangedLocation: true,
    }
  },
}

// ------------------------------------
// Reducer
// ------------------------------------

const initialState = {
  isAuthenticated: false,
  isCurrentUserLoading: true,
  submitting: undefined,
  userInitialized: false,
  numberOfFailedLoginAttempts: 0,
}

export default function user(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------
// Best-effort: sync the visitor's consent to their account. Fire-and-forget —
// never blocks or fails the calling flow.
//   - A confirmed choice always syncs (overrides any prior stored choice).
//   - An unconfirmed default is only recorded when the user has no record yet
//     (creation / first-seen), so it can never overwrite a prior opt-out made on
//     another device.
export function* syncUserConsent() {
  try {
    // Wait for airgap to finish loading before reading consent — right after
    // login/signup it may still be initializing, which would otherwise yield an
    // empty body and skip recording the creation default.
    yield call(whenAirgapReady)
    const body = getConsentSyncBody()
    if (!body) {
      return
    }
    const token = yield call(getDecodedAccessToken)
    const userId = token?.user_id
    if (!userId) {
      return
    }

    if (!body.confirmed) {
      // Only record a default when the user has no consent record yet
      // (creation / first-seen); never overwrite a prior choice.
      let recordExists = true
      try {
        yield call(getUserConsent, userId)
      } catch (ex) {
        const status = ex?.code ?? ex?.response?.status
        if (status !== 404) {
          throw ex
        }
        recordExists = false
      }
      if (recordExists) {
        return
      }
    }

    yield call(updateUserConsent, userId, body)
  } catch (ex) {
    logException(ex)
  }
}

// Re-syncs consent to the account whenever a logged-in user changes their choice
// (e.g. via the "Your Privacy Choices" footer link) — not just at login.
// Anonymous changes stay in localStorage and sync on their next login.
function* syncConsentIfAuthenticated() {
  const isAuthenticated = yield select(selectIsAuthenticated)
  if (isAuthenticated) {
    yield call(syncUserConsent)
  }
}

export function* watchConsentChanges() {
  const channel = eventChannel(emit => onConsentChange(() => emit(true)))
  // takeLatest: a newer consent change cancels an in-flight sync, so rapid
  // toggles can't land out of order and clobber a newer opt-out.
  yield takeLatest(channel, syncConsentIfAuthenticated)
}

export function* watchLogin() {
  while (true) {
    const {
      payload: { credentials, ref },
    } = yield take(LOGIN)
    try {
      const selector = yield select(selectRef)
      const connectionId = credentials.connectionId
      yield call(formSaga, 'login', loginUser, [credentials], loginSuccess, loginFailure)
      const isAuthenticated = yield select(selectIsAuthenticated)

      if (isAuthenticated) {
        yield fork(syncUserConsent)
      }

      let url
      // Do any redirects
      if (ref) {
        url = ref
      } else if (selector?.ref) {
        url = selector.ref
      } else if (isAuthenticated && connectionId) {
        url = friendsRoute.path + `?connectionId=${connectionId}`
      }

      if (url) {
        yield put(push(url))
      }
    } catch (ex) {
      logException(ex)
      yield put(loginFailure(ex))
    }
  }
}

export function* watchUserInfoLoaded() {
  while (true) {
    yield race([take(LOGIN_SUCCESS), take(GET_CURRENT_USER_SUCCESS)])
    const currentUser = yield select(selectCurrentUser)
    if (currentUser) {
      yield put(loadUserSavedLocationsStart())
    }

    yield take(LOAD_LOCATION_SUCCESS)
    try {
      const location = yield select(selectCurrentLocationWithAddress)
      if (currentUser) {
        if (location) {
          storeUserInfoCookie({
            userBuildingCity: location.city,
            userBuildingEPP: location.isEpp,
            userBuildingId: location.deliveryLocationId,
            userBuildingName: location.deliveryLocationName,
            userBuildingState: location.state,
            userBuildingStreet: location.street,
            userBuildingZip: location.zip,
            userId: currentUser.userId,
          })
        } else {
          storeUserInfoCookie({
            userId: currentUser.userId,
          })
        }
      }
    } catch (ex) {
      logException(ex)
    }
  }
}

export function* watchLogout() {
  while (true) {
    const {
      payload: { currentUser },
    } = yield take(LOGOUT)

    resetCaches()

    try {
      const id = yield select(getLocationIdParam)
      const route = id ? null : loginRoute.path

      try {
        if (currentUser && (currentUser.authorities || []).includes(ROLE_FOODSBY_ADMIN)) {
          try {
            yield call(logoutAdmin)
          } catch (ex) {
            logException(ex)
            // don't throw a fail state, just continue
          }
        }
        yield call(logout, process.env.REACT_APP_COOKIE_NAME, process.env.REACT_APP_COOKIE_DOMAIN)
        yield put(logoutSuccess())

        if (route) {
          // refreshes the whole app so the state gets cleared
          window.location.href = route
        }
      } catch (ex) {
        logException(ex)
        yield put(logoutFailure(ex))
      }
    } catch (ex) {
      logException(ex)
      yield put(logoutFailure(ex))
      window.location.href = loginRoute.path
    }
  }
}

export function* watchLoadCurrentUser() {
  while (true) {
    yield take(GET_CURRENT_USER)
    try {
      const token = yield call(getDecodedAccessToken)
      if (token) {
        yield put(
          currentUserSuccess({
            authorities: token.authorities,
            deliveryLocationId: token.location_id,
            email: token.user_name,
            userId: token.user_id,
            userName: token.user_name,
          }),
        )
        if (window.braze) {
          window.braze.changeUser(token.user_id) // start tracking in braze
        }
      } else {
        yield put(currentUserFailure())
      }
    } catch (ex) {
      yield put(currentUserFailure(ex))
    }
  }
}

export function* watchConfirmUser() {
  while (true) {
    const {
      payload: { entryPoint, formData, referralCode },
    } = yield take(CONFIRM_USER)
    const reduxEntryPoint = yield select(selectEntryPoint)
    // Referral links land on the home/login flow rather than a dedicated sign up
    // page, so the code is captured at app start and read back here.
    const reduxReferralCode = yield select(selectReferralCode)
    formData.locationId = Number(formData.locationId)
    formData.platform = 'WEB'
    formData.entryPoint = entryPoint || reduxEntryPoint || 'SELF_SIGN_UP'

    yield call(
      formSaga,
      'confirmregister',
      oneShotRegister,
      [formData],
      payload => confirmRegisterSuccess(payload, referralCode || reduxReferralCode),
      confirmRegisterFailure,
    )
  }
}

export function* watchSubmitRegisterLight() {
  while (true) {
    const {
      payload: {
        userData: { locationId, username },
      },
    } = yield take(REGISTER_LIGHT)
    try {
      yield call(
        formSaga,
        'registrationLight',
        joinUser,
        [username, locationId],
        registerLightSuccess,
        registerLightFailure,
      )
    } catch (ex) {
      yield put(registerLightFailure(ex))
    }
  }
}

export function* watchRegisterLightSuccess() {
  while (true) {
    const {
      payload: { username },
    } = yield take(REGISTER_LIGHT_SUCCESS)
    yield put(loginStart({ password: '', userName: username }))
  }
}

export function* watchConfirmUserSuccess() {
  while (true) {
    const {
      payload: { path },
    } = yield take(CONFIRM_USER)
    yield take(CONFIRM_USER_SUCCESS)
    const credentials = yield select(selectConfirmUserCredentials)

    yield put(
      loginStart(
        {
          password: credentials.password,
          userName: credentials.email,
        },
        path,
      ),
    )
  }
}

export function* watchRegisterSuccess() {
  while (true) {
    const results = yield all([
      take(CONFIRM_USER_SUCCESS),
      take(FULFILLED(PROFILE_SET)),
      take(LOGIN_SUCCESS),
      take(LOAD_LOCATION_SUCCESS),
    ])
    const currentUser = yield select(selectCurrentUser)
    const location = yield select(selectCurrentLocationWithAddress)
    const referralCode = first(results)?.payload?.referralCode

    if (currentUser && location) {
      yield put(createUserSavedLocationStart(location))
    }
    if (isString(referralCode) && !isEmpty(referralCode)) {
      try {
        const result = yield call(referralRedeem, referralCode)
        yield put(confirmReferralSuccess(result))
      } catch (ex) {
        yield put(
          enqueueSnackbar({
            message: `An error occurred. ${ex?.message}`,
          }),
        )
      } finally {
        // The attempt is over either way. Leaving the code behind would re-apply
        // it to the next sign up in this tab.
        clearPendingReferralCode()
        yield put(setReferralCode(undefined))
      }
    }
    if (currentUser) {
      pushGTMDataLayer({
        event: 'completedRegistration',
        signupSource: 'webApp',
        userId: currentUser.userId,
      })
      if (window.braze) {
        window.braze.changeUser(currentUser.userId)
      }
    }
  }
}

export function* watchUpdateAndSaveUserLocation() {
  while (true) {
    const {
      payload: { locationId },
    } = yield take(UPDATE_AND_SAVE_USER_LOCATION)
    const user = yield select(selectCurrentUser)

    try {
      // update the user's location via API
      yield call(apiSaga, updateUserLocation, [user.userId, locationId])

      user.deliveryLocationId = locationId
      // update user token
      yield call(refreshTokenSaga)
      yield put(updateAndSaveUserLocationSuccess(user))
      yield put(clearPreviousLocation())
    } catch (ex) {
      yield put(updateAndSaveUserLocationFailure(ex))
    }
  }
}

export function* watchUpdateAndSaveUserLocationSuccess() {
  while (true) {
    const {
      payload: {
        currentUser: { deliveryLocationId },
      },
    } = yield take(UPDATE_AND_SAVE_USER_LOCATION_SUCCESS)
    yield put(loadLocationStart(deliveryLocationId))
  }
}

export function* watchRevertTemporaryLocation() {
  while (true) {
    const {
      payload: { originalLocationId },
    } = yield take(REVERT_TEMPORARY_LOCATION)
    yield put(push(formatUrl(locationRoute.path, { locationId: originalLocationId })))
  }
}
